/**
 * @file ReceiptReasonFields.tsx
 * @description «Причина приёмки» в шапке черновика и «Парное списание» для
 * внутреннего выпуска (phase19.22, W17-9b, W19).
 *
 * Собственная продукция до phase19.21 оформляется парой документов:
 * списание «В производство» (сырьё ушло) + приёмка «Внутренний выпуск»
 * (готовое пришло). Пара связывается здесь: список — списания склада с
 * причиной «В производство». Причина определяет происхождение партии
 * (закупка / производство / корректировка) — поэтому поле в шапке, до строк.
 */

import { useEffect, useState } from "react";

import { RECEIPT_REASON_FIELDS_ID } from "./receipt-commit-block";
import { WmsFieldLabel } from "./WmsFieldLabel";
import { SelectField } from "../ui/SelectField";
import {
  RECEIPT_REASON_LABEL,
  writeoffsApi,
  type GoodsReceiptReason,
  type WriteoffDto,
} from "../../lib/api-wms";

interface Props {
  warehouseId: string;
  reason: GoodsReceiptReason;
  parentWriteoffId: string | null;
  onChange: (reason: GoodsReceiptReason, parentWriteoffId: string | null) => void;
  disabled?: boolean;
}

const REASON_OPTIONS = (Object.keys(RECEIPT_REASON_LABEL) as GoodsReceiptReason[]).map((value) => ({
  value,
  label: RECEIPT_REASON_LABEL[value],
}));

export function ReceiptReasonFields({ warehouseId, reason, parentWriteoffId, onChange, disabled }: Props) {
  const internal = reason === "INTERNAL_OUTPUT";
  const [writeoffs, setWriteoffs] = useState<WriteoffDto[]>([]);

  useEffect(() => {
    if (!internal) return;
    let cancelled = false;
    writeoffsApi
      .listPage({ warehouseId, reason: "PRODUCTION_INPUT", pageSize: 50 })
      .then((page) => {
        if (!cancelled) setWriteoffs(page.items);
      })
      .catch(() => {
        if (!cancelled) setWriteoffs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [internal, warehouseId]);

  return (
    <div
      id={RECEIPT_REASON_FIELDS_ID}
      className="grid grid-cols-1 gap-3 rounded-xl bg-surface-lowest p-4 ring-ghost sm:grid-cols-2"
    >
      <WmsFieldLabel label="Причина приёмки">
        <SelectField
          value={reason}
          onChange={(next) => onChange(next as GoodsReceiptReason, null)}
          options={REASON_OPTIONS}
          ariaLabel="Причина приёмки"
          disabled={disabled}
        />
      </WmsFieldLabel>
      {internal ? (
        <WmsFieldLabel label="Парное списание «В производство»">
          <SelectField
            value={parentWriteoffId ?? ""}
            onChange={(next) => onChange(reason, next || null)}
            options={[
              { value: "", label: "— не выбрано —" },
              ...writeoffs.map((w) => ({
                value: w.id,
                label: `${w.writeoffNumber} · ${w.status === "DRAFT" ? "черновик" : "проведено"}`,
              })),
            ]}
            placeholder="— не выбрано —"
            ariaLabel="Парное списание"
            disabled={disabled}
          />
          {writeoffs.length === 0 ? (
            <span className="text-xs text-on-surface-variant">
              Списаний «В производство» на складе нет — оформите списание сырья и вернитесь.
            </span>
          ) : null}
        </WmsFieldLabel>
      ) : null}
    </div>
  );
}
